/**
 * 公開コメントのパネル（EP-1 公開データ・M6c）。
 *
 * 承認済みのコメントを投稿順に並べ、ログイン中なら投稿フォームを出す。
 * 既定は pre-moderation なので、投稿しても一覧にはすぐ出ない — 受け付けたことは
 * `aria-live` の状態表示で伝える。
 *
 * 本文は必ず `textContent` で描く（サーバは生の本文を保存するため、ここが表示側の
 * 唯一のエスケープ点になる）。
 */
import { cls } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { ViewerState } from '../core/viewer';
import type { Translate } from '../i18n/i18n';
import type { CommentItem } from '../user-data/comments';
import type { ViewerApi } from './api';
import type { PanelContent } from './panels';

export class CommentsPanel implements PanelContent {
  readonly el: HTMLElement;

  private readonly status: HTMLParagraphElement;
  private readonly list: HTMLUListElement;
  private readonly form: HTMLFormElement;
  private readonly input: HTMLTextAreaElement;
  private readonly signIn: HTMLParagraphElement;
  private controller: AbortController | null = null;
  private posting = false;

  constructor(
    private readonly doc: Document,
    private readonly registry: DisposeRegistry,
    private readonly api: ViewerApi,
    private readonly translate: Translate,
  ) {
    this.el = doc.createElement('div');
    this.el.className = cls('comments');

    this.status = doc.createElement('p');
    this.status.className = cls('comments-status');
    this.status.setAttribute('aria-live', 'polite');

    this.list = doc.createElement('ul');
    this.list.className = cls('comments-list');

    this.input = doc.createElement('textarea');
    this.input.className = cls('comments-input');
    this.input.rows = 3;
    // name の無い欄は DevTools が指摘する（id は複数ビューワで衝突するので使わない）。
    this.input.name = 'comment';
    this.input.setAttribute('aria-label', translate('comments.label'));
    this.input.placeholder = translate('comments.placeholder');

    const submit = doc.createElement('button');
    submit.type = 'submit';
    submit.className = `${cls('button')} ${cls('comments-submit')}`;
    submit.textContent = translate('comments.submit');

    this.form = doc.createElement('form');
    this.form.className = cls('comments-form');
    this.form.hidden = true;
    this.form.append(this.input, submit);
    this.registry.listen(this.form, 'submit', (event) => {
      event.preventDefault();
      void this.submit();
    });

    this.signIn = doc.createElement('p');
    this.signIn.className = cls('comments-signin');
    this.signIn.textContent = translate('comments.signIn');
    this.signIn.hidden = true;

    this.el.append(this.status, this.list, this.form, this.signIn);
  }

  /** パネルを開いたら一覧を取り直す（承認されたコメントが増えている可能性がある）。 */
  activate(): void {
    // sync() は開いているパネルにしか届かないので、開いた時点の状態をここで一度反映する
    this.sync(this.api.getState());
    void this.refresh();
  }

  sync(state: ViewerState): void {
    this.form.hidden = !state.signedIn;
    this.signIn.hidden = state.signedIn;
  }

  dispose(): void {
    this.controller?.abort();
    this.el.remove();
  }

  private async refresh(): Promise<void> {
    const comments = this.api.comments();
    if (!comments) return;

    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.status.textContent = this.translate('comments.loading');

    const items = await comments.list(controller.signal);
    if (controller.signal.aborted) return;
    this.render(items);
  }

  private render(items: readonly CommentItem[]): void {
    this.status.textContent = items.length === 0 ? this.translate('comments.empty') : '';
    const fragment = this.doc.createDocumentFragment();
    for (const item of items) fragment.appendChild(this.row(item));
    this.list.replaceChildren(fragment);
  }

  private row(item: CommentItem): HTMLLIElement {
    const li = this.doc.createElement('li');
    li.className = cls('comment');

    const name = this.doc.createElement('span');
    name.className = cls('comment-name');
    name.textContent = item.name ?? this.translate('comments.anonymous');

    const body = this.doc.createElement('p');
    body.className = cls('comment-body');
    body.textContent = item.body; // 生の本文は必ずテキストとして描く

    li.append(name, body);
    return li;
  }

  private async submit(): Promise<void> {
    const comments = this.api.comments();
    const body = this.input.value.trim();
    if (!comments || body === '' || this.posting) return;

    this.posting = true;
    const result = await comments.post(body);
    this.posting = false;
    if (result === null) {
      this.status.textContent = this.translate('comments.failed');
      return;
    }

    this.input.value = '';
    // post 運用なら即座に一覧へ出る。既定（pre-moderation）では出ないので、
    // どちらの場合も「受け付けた／公開した」を状態表示で伝える（一覧の後に上書きする）
    if (result.status === 'approved') await this.refresh();
    this.status.textContent = this.translate(
      result.status === 'approved' ? 'comments.published' : 'comments.pending',
    );
  }
}
